'use client';

import { isNotEmpty } from '@newfront-insurance/core';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';

import { cn } from '../../lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../../primitives/command';
import { Input } from '../../primitives/input';
import { Popover, PopoverContent, PopoverAnchor } from '../../primitives/popover';

const comboboxInputVariants = cva('relative w-full');

interface ComboboxInputProps extends React.ComponentProps<typeof Input> {
  /**
   * Array of suggestion strings to show in dropdown
   */
  suggestions?: string[];
  /**
   * Callback when user selects a suggestion
   */
  onSelectSuggestion?: (suggestion: string) => void;
  /**
   * Text to show when no suggestions match. If not provided, no empty text will be shown.
   */
  emptyText?: string;
  /**
   * Maximum number of suggestions to show
   */
  maxSuggestions?: number;
  /**
   * Whether to show dropdown on focus
   */
  showOnFocus?: boolean;
  /**
   * Minimum characters before showing suggestions
   */
  minChars?: number;
}

export const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputProps>(
  (
    {
      suggestions = [],
      value = '',
      onSelectSuggestion,
      emptyText,
      maxSuggestions,
      showOnFocus = true,
      minChars = 0,
      className,
      onFocus,
      onBlur,
      onChange,
      ...inputProps
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const commandRef = useRef<HTMLDivElement>(null);
    const popoverContentRef = useRef<HTMLDivElement>(null);

    // Expose input ref to parent
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

    // Helper function to filter suggestions based on a given value
    const getFilteredSuggestions = (inputValue: string): string[] => {
      const stringInputValue = String(inputValue || '');
      if (!isNotEmpty(stringInputValue) || stringInputValue.length < minChars) {
        return maxSuggestions ? suggestions.slice(0, maxSuggestions) : suggestions;
      }

      const filtered = suggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(stringInputValue.toLowerCase()),
      );
      return maxSuggestions ? filtered.slice(0, maxSuggestions) : filtered;
    };

    // Filter suggestions based on current input value
    const stringValue = String(value || '');
    const filteredSuggestions = getFilteredSuggestions(stringValue);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange?.(e);

      // Calculate filtered suggestions for the NEW value (not the stale state)
      const newFilteredSuggestions = getFilteredSuggestions(newValue);

      // Show dropdown only if there are actual suggestions for the new value
      if (newFilteredSuggestions.length > 0 && newValue.length >= minChars) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      // Use current filtered suggestions for focus behavior
      if (showOnFocus && filteredSuggestions.length > 0 && stringValue.length >= minChars) {
        setOpen(true);
      }
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      // Use setTimeout to allow for potential focus to move to popover content
      // Only close if focus hasn't moved to the popover
      setTimeout(() => {
        const { activeElement } = document;
        if (!commandRef.current?.contains(activeElement) && !inputRef.current?.contains(activeElement)) {
          setOpen(false);
        }
      }, 0);
    };

    const handleSelectSuggestion = (suggestion: string) => {
      onChange?.({ target: { value: suggestion } } as React.ChangeEvent<HTMLInputElement>);
      onSelectSuggestion?.(suggestion);

      // Focus back on input
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) {
        // If dropdown is closed, only handle Escape
        if (e.key === 'Escape') {
          setOpen(false);
        }
        return;
      }

      // When dropdown is open, handle navigation keys
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          // Keep focus on input
          inputRef.current?.focus();
          break;

        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          // Focus Command and forward the keyboard event to enable native navigation
          if (commandRef.current) {
            // Ensure Command has focus to receive keyboard events
            commandRef.current.focus();

            // Create and dispatch the same keyboard event on Command
            const keyEvent = new KeyboardEvent('keydown', {
              key: e.key,
              code: e.code,
              bubbles: true,
              cancelable: true,
              ctrlKey: e.ctrlKey,
              altKey: e.altKey,
              shiftKey: e.shiftKey,
              metaKey: e.metaKey,
            });

            // Dispatch the event to let CMDK handle navigation
            commandRef.current.dispatchEvent(keyEvent);
          }
          break;

        case 'Enter':
          e.preventDefault();
          // Forward Enter key to Command to trigger selection
          if (commandRef.current) {
            const keyEvent = new KeyboardEvent('keydown', {
              key: e.key,
              code: e.code,
              bubbles: true,
              cancelable: true,
              ctrlKey: e.ctrlKey,
              altKey: e.altKey,
              shiftKey: e.shiftKey,
              metaKey: e.metaKey,
            });

            // Dispatch Enter to let CMDK handle selection
            commandRef.current.dispatchEvent(keyEvent);
          }
          break;

        default:
          // For other keys (typing), let the input handle normally
          break;
      }
    };

    const handleInputClick = () => {
      // Use current filtered suggestions for click behavior
      if (showOnFocus && filteredSuggestions.length > 0 && stringValue.length >= minChars) {
        setOpen(true);
      }
    };

    // Close popover on scroll (similar to Floating UI's ancestorScroll behavior)
    useEffect(() => {
      if (!open) return;

      const handleScroll = (event: Event) => {
        // Don't close if the scroll is happening within the popover content itself
        if (popoverContentRef.current && event.target instanceof Node) {
          // Check if the scroll target is inside the popover content
          if (popoverContentRef.current.contains(event.target) || popoverContentRef.current === event.target) {
            return;
          }
        }
        setOpen(false);
      };

      // Listen for scroll events on the document and scrollable ancestors
      // Use capture: true to catch all scroll events in the tree
      document.addEventListener('scroll', handleScroll, { passive: true, capture: true });

      return () => {
        document.removeEventListener('scroll', handleScroll, { capture: true });
      };
    }, [open]);

    return (
      <div className={cn(comboboxInputVariants(), className)}>
        <Popover open={open}>
          <PopoverAnchor asChild>
            <Input
              ref={inputRef}
              value={value}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={open ? 'combobox-list' : undefined}
              aria-autocomplete="both"
              {...inputProps}
            />
          </PopoverAnchor>
          <PopoverContent
            ref={popoverContentRef}
            className="w-(--radix-popper-anchor-width) p-0"
            align="start"
            side="bottom"
            sideOffset={4}
            onOpenAutoFocus={(e) => {
              // Prevent popover from stealing focus from input
              e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
              // Prevent popover from interfering with input focus
              e.preventDefault();
            }}
          >
            <Command ref={commandRef} shouldFilter={false} tabIndex={0}>
              <CommandList id="combobox-list" role="listbox">
                {filteredSuggestions.length === 0 && emptyText ? (
                  <CommandEmpty>{emptyText}</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filteredSuggestions.map((suggestion) => (
                      <CommandItem
                        key={suggestion}
                        value={suggestion}
                        onSelect={() => handleSelectSuggestion(suggestion)}
                        className="cursor-pointer"
                        role="option"
                      >
                        {suggestion}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
